import { IQuoteOfferingRespSchema } from '@monetize/types/app';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  doGetOfferingRate,
  productCatalogServiceQueryKeys,
} from '../../../../../api/productCatalogService';
import { getQuoteOfferingRateIds } from './quoteOfferingUtils';

const NO_QUOTE_OFFERINGS: IQuoteOfferingRespSchema[] = [];

/**
 * Warms the by-id rate cache for every rate on the quote in one pass, so each
 * offering row finds its rate already cached on its first render instead of
 * fetching it itself and settling at a different moment from its neighbours.
 *
 * Shares the query key with useOfferingRate, which hydrates from the same cache
 * and fetches through it, so the two can never duplicate a request for the same
 * rate. `isPending` is true until every rate has either loaded or failed.
 *
 * Pass `enabled: false` once the rows are mounted. They keep their own
 * observers from then on, so leaving these mounted only adds background
 * refetches for rates nothing is displaying.
 */
export const useQuoteOfferingRatesPrefetch = (
  quoteOfferings: IQuoteOfferingRespSchema[] = NO_QUOTE_OFFERINGS,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const rateIds = useMemo(
    () => getQuoteOfferingRateIds(quoteOfferings),
    [quoteOfferings],
  );

  return useQueries({
    queries: rateIds.map((rateId) => ({
      queryKey: productCatalogServiceQueryKeys.ratesById(rateId),
      queryFn: () => doGetOfferingRate(rateId),
      enabled,
      // A rate that will not load is the row's problem to surface; the section
      // should not sit on its skeleton retrying it.
      retry: false,
    })),
    combine: (results) => ({
      isPending: results.some(({ isPending }) => isPending),
    }),
  });
};
